import React, { useState, useRef, useContext, useCallback, useMemo, useEffect } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { VariableSizeGrid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import CardData from './CardData';
import { IFigureDto } from '../models/IFigureDto';
import FigurService from '../services/figureService';
import Breadcrumbs from './Breadcrumbs';
import Header from './Header';
import { IFigureCollection } from '../models/IFigureCollection';
import { FavoritesContext } from '../context/FavoritesContext';
import { useConfig } from '../config/env';
import { useElementWidth } from '../hooks/useElementWidth';
import { useGridFigures } from '../hooks/useGridFigures';

interface CardViewProps {
  collectionId: number;
  view: 'list' | 'grid';
  onViewChange: (event: React.MouseEvent<HTMLElement>, newView: 'list' | 'grid' | null) => void;
  onFigureSelect: (figure: IFigureDto) => void;
  onForceGridView?: () => void;
  sharedSearchState?: {
    results: IFigureDto[];
    isSearchMode: boolean;
    totalCount: number;
    collectionId: number | null;
  } | null;
  onAvatarClick: (collectionId: number) => void;
  seriesId?: number | null;
  selectedTags?: string[];
  collections?: Array<{ id: number; name: string; color?: string }>;
}

const CardView: React.FC<CardViewProps> = ({
  collectionId,
  view,
  onViewChange,
  onFigureSelect,
  onForceGridView,
  sharedSearchState,
  onAvatarClick,
  seriesId,
  selectedTags = [],
  collections = [],
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<VariableSizeGrid>(null);
  const { refreshFavorites } = useContext(FavoritesContext);
  const [breadcrumbItems, setBreadcrumbItems] = useState<{ label: string; href?: string; onClick?: () => void }[]>([]);
  const [currentCollection, setCurrentCollection] = useState<IFigureCollection>();
  const [totalLoaded, setTotalLoaded] = useState<number>(0);
  const [totalAvailable, setTotalAvailable] = useState<number>(0);

  const defaultRowHeight = 430;

  // Use shared search state for this collection
  const effectiveSearchResults = sharedSearchState?.collectionId === collectionId ? sharedSearchState.results : [];
  const effectiveIsSearchMode = sharedSearchState?.collectionId === collectionId ? sharedSearchState.isSearchMode : false;
  const effectiveSearchTotalCount = sharedSearchState?.collectionId === collectionId ? sharedSearchState.totalCount : 0;

  // Card dimensions
  const CARD_MIN_WIDTH = 250; // Minimum card width in pixels
  const CARD_ASPECT_RATIO = 1.35; // Height to width ratio for cards
  const CARD_GAP = 10; // Gap between cards in pixels

  const cfg = useConfig();

  const {
    figures,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    totalLoaded: hookTotalLoaded,
    totalAvailable: hookTotalAvailable,
    fetchNextPage,
  } = useGridFigures({
    collectionId,
    seriesId,
    selectedTags,
    sharedSearchState,
    favoritterCollectionId: Number(cfg?.favoritterCollectionId ?? 0),
    defaultCollectionId: Number(cfg?.defaultCollectionId ?? 0),
  });

  const loadMoreItems = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      // eslint-disable-next-line no-console
      console.log('Loading more items...');
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const containerWidth = useElementWidth(containerRef);

  useEffect(() => {
    setTotalLoaded(hookTotalLoaded);
    setTotalAvailable(hookTotalAvailable);
  }, [hookTotalLoaded, hookTotalAvailable]);

  useEffect(() => {
    const updateBreadcrumbs = async () => {
      try {
        if (collectionId === Number(cfg?.favoritterCollectionId ?? 0)) {
          setBreadcrumbItems([{ label: 'Favoritter' }]);
        } else {
          const collection = await FigurService.getCollectionsByCustomer(1);
          const foundCollection = collection.find((c) => c.id === collectionId);
          setCurrentCollection(foundCollection);
          if (foundCollection) {
            const defaultId = Number(cfg?.defaultCollectionId ?? 0);
            setBreadcrumbItems([
              { label: 'Byggforskserien', onClick: defaultId > 0 ? () => onAvatarClick(defaultId) : undefined },
              { label: foundCollection.name },
            ]);
          }
        }
      } catch (error) {
        console.error('Error updating breadcrumbs:', error);
      }
    };

    if (!effectiveIsSearchMode) {
      updateBreadcrumbs();
    }
  }, [collectionId, effectiveIsSearchMode, cfg?.favoritterCollectionId, cfg?.defaultCollectionId, onAvatarClick]);

  useEffect(() => {
    refreshFavorites();
  }, [collectionId, refreshFavorites]);

  const getRowHeight = useCallback(
    (index: number) => {
      return defaultRowHeight;
    },
    [defaultRowHeight],
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        minHeight: 0,
        opacity: isLoading ? 0 : 1,
        transition: 'opacity 0.2s ease-in-out',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}>
      <Box
        ref={containerRef}
        component="main"
        sx={{
          width: '100%',
          maxWidth: 'none',
          margin: '0 auto',
          boxSizing: 'border-box',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Box sx={{ py: 2, px: 0 }}>
          <Breadcrumbs
            items={
              effectiveIsSearchMode
                ? [
                    {
                      label: 'Byggforskserien',
                      onClick: Number(cfg?.defaultCollectionId ?? 0) > 0 ? () => onAvatarClick(Number(cfg?.defaultCollectionId ?? 0)) : undefined,
                    },
                    { label: 'Søkeresultat' },
                  ]
                : breadcrumbItems
            }
          />
        </Box>

        <Box sx={{ p: 0 }}>
          <Header
            title={
              effectiveIsSearchMode
                ? 'Søkeresultat'
                : collectionId === Number(cfg?.favoritterCollectionId ?? 0)
                ? 'Favoritter'
                : currentCollection?.name ?? 'Kortvisning'
            }
            description={effectiveIsSearchMode ? `${effectiveSearchTotalCount} resultater` : currentCollection?.headerComment ?? ''}
            view={view}
            onViewChange={onViewChange}
            disableEditing={effectiveIsSearchMode}
            onDescriptionChange={
              effectiveIsSearchMode
                ? async () => {}
                : async (newDesc) => {
                    if (!currentCollection) return;

                    try {
                      const updatedCollection = await FigurService.updateCollectionDetails(
                        currentCollection.id,
                        currentCollection.name,
                        1, // Hardcoded customerId - needs proper value
                        'random comment',
                        newDesc,
                      );
                      setCurrentCollection(updatedCollection);
                    } catch (error) {
                      console.error('Error updating collection header:', error);
                    }
                  }
            }
          />

          {/* Display count */}
          {figures.length > 0 && (
            <Box sx={{ mt: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
              {effectiveIsSearchMode
                ? `Viser ${effectiveSearchResults.length} av ${effectiveSearchTotalCount} søkeresultater`
                : `Viser ${totalLoaded} av ${totalAvailable} figurer`}
            </Box>
          )}

          <Box sx={{ mt: 2, flex: 1, height: 'calc(100vh - 320px)', minHeight: 0, bgcolor: '#f6f7f8', pr: 0 }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : figures.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                {effectiveIsSearchMode ? 'Ingen søkeresultater funnet' : 'Ingen figurer funnet'}
              </Box>
            ) : (
              <AutoSizer>
                {({ height, width }: { height: number; width: number }) => {
                  // Use DOM-measured container width when available so grid math and rendered width match
                  const effectiveWidth = containerRef.current?.getBoundingClientRect().width ?? width;

                  // compute columns & widths from the effectiveWidth
                  const columnCountLocal = Math.max(1, Math.floor((effectiveWidth - CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP)));
                  const totalGaps = (columnCountLocal + 1) * CARD_GAP;
                  const totalAvailable = Math.max(0, effectiveWidth - totalGaps);
                  const baseWidthLocal = Math.floor(totalAvailable / columnCountLocal);
                  const remainderLocal = totalAvailable - baseWidthLocal * columnCountLocal;
                  const getColumnWidthLocal = (index: number) => baseWidthLocal + (index < remainderLocal ? 1 : 0);
                  const rowCountLocal = Math.ceil(figures.length / columnCountLocal);

                  const CellRendererLocal = ({
                    columnIndex,
                    rowIndex,
                    style,
                  }: {
                    columnIndex: number;
                    rowIndex: number;
                    style: React.CSSProperties;
                  }) => {
                    const index = rowIndex * columnCountLocal + columnIndex;
                    if (index >= figures.length) return null;
                    const figure = figures[index];

                    // center last row if it's not full
                    const lastRowIndex = Math.max(0, rowCountLocal - 1);
                    //let adjustedStyle: React.CSSProperties = { ...style };
                    let adjustedStyle: React.CSSProperties = { ...style, padding: CARD_GAP / 2, boxSizing: 'border-box' };

                    // if (rowIndex === lastRowIndex) {
                    //   const itemsInLastRow = figures.length % columnCountLocal || columnCountLocal;
                    //   if (itemsInLastRow < columnCountLocal) {
                    //     const emptySlots = columnCountLocal - itemsInLastRow;
                    //     const slotWidth = totalAvailable / columnCountLocal + CARD_GAP;
                    //     const shift = Math.round((emptySlots * slotWidth) / 2);
                    //     const leftVal = typeof style.left === 'number' ? style.left : parseFloat(String(style.left || 0));
                    //     adjustedStyle = { ...adjustedStyle, left: leftVal + shift };
                    //   }
                    // }

                    adjustedStyle = { ...adjustedStyle, padding: CARD_GAP / 2, boxSizing: 'border-box' };

                    return (
                      <div style={adjustedStyle}>
                        <CardData
                          figure={figure}
                          collectionId={collectionId}
                          onFigureSelect={onFigureSelect}
                          onAvatarClick={onAvatarClick}
                          collections={collections}
                          isFavoritesCollection={collectionId === Number(cfg?.favoritterCollectionId ?? 0)}
                          isDefaultCollection={currentCollection?.isDefault ?? collectionId === Number(cfg?.defaultCollectionId ?? 0)}
                        />
                      </div>
                    );
                  };

                  return (
                    <VariableSizeGrid
                      ref={gridRef}
                      columnCount={columnCountLocal}
                      columnWidth={getColumnWidthLocal}
                      height={height}
                      rowCount={rowCountLocal}
                      rowHeight={getRowHeight}
                      width={effectiveWidth}
                      onItemsRendered={({
                        visibleRowStopIndex,
                        visibleColumnStopIndex,
                      }: {
                        visibleRowStopIndex: number;
                        visibleColumnStopIndex: number;
                      }) => {
                        // Flatten 2D visible indices into a single visible stop index
                        const visibleStopIndex = visibleRowStopIndex * columnCountLocal + visibleColumnStopIndex;
                        const remaining = figures.length - visibleStopIndex - 1;

                        // When within ~2 rows of the end, request next page
                        if (remaining <= columnCountLocal * 2 && hasNextPage && !isFetchingNextPage) {
                          loadMoreItems();
                        }
                      }}
                      overscanRowCount={5}
                      overscanColumnCount={2}>
                      {CellRendererLocal}
                    </VariableSizeGrid>
                  );
                }}
              </AutoSizer>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CardView;
